import { SelectOption } from '../components/settings/Options'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Item } from './Item'
import { FKType } from './SQLBuilder'
import { Transactable } from './Transactable'
import { Transaction } from './Transaction'

type AdjustmentConstructorArgs = ConstructorArgs<() => Adjustment>

export class Adjustment extends Transaction implements Transactable {
    readonly recordType = 'adjustment'

    readonly iTransactable = true

    type: string | undefined

    constructor(data?: Partial<AdjustmentConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.type = data.type ?? undefined
    }

    clone(overrides?: Partial<AdjustmentConstructorArgs>): Adjustment {
        return new Adjustment({ ...this.serializeForClone(), ...overrides })
    }

    static initWithItem(item: Item): Adjustment {
        const adjustment = new Adjustment()
        adjustment.item = item
        adjustment.total = item.rate
        adjustment.lines = []
        return adjustment
    }

    get amount(): number | undefined {
        return this.total
    }

    get paidAmount(): number | undefined {
        return this.paidDate != undefined ? this.amount : 0
    }

    get owedAmount(): number | undefined {
        return this.paidDate != undefined || this.cancelledDate != undefined ? 0 : this.amount
    }

    get taxAmount(): number | undefined {
        return 0
    }

    get statusString(): string | undefined {
        if (this.cancelledDate != undefined)
            return `canceled on ${this.cancelledDate.format('MMM D')}, ${this.cancelledDate.format('YYYY')}`
        if (this.paidDate != undefined)
            return `purchased on ${this.paidDate.format('MMM D')}, ${this.paidDate.format('YYYY')}`
        return ''
    }

    get adjustmentSign(): 1 | -1 {
        return ['resupply', 'recount'].includes(this.type ?? '') ? 1 : -1
    }

    get quantity(): number | undefined {
        return this.lines[0]?.quantity
    }

    get adjustmentString(): string {
        switch (this.type ?? '') {
            case 'resupply':
                return 'Resupply'
            case 'recount':
                return 'Recount'
            case 'sold':
                return 'Sold'
            case 'lost':
                return 'Lost'
            case 'expired':
                return 'Expired'
            case 'internal':
                return 'Internal use'
            default:
                return ''
        }
    }

    static adjustmentStrings(): SelectOption[] {
        return [
            { value: 'resupply', label: 'Resupply (Add)' },
            { value: 'recount', label: 'Recount (Add)' },
            { value: 'sold', label: 'Sold (Reduce)' },
            { value: 'lost', label: 'Lost (Reduce)' },
            { value: 'expired', label: 'Expired (Reduce)' },
            { value: 'internal', label: 'Internal use (Reduce)' },
        ]
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.item) this.item = BaseRecord.fromSerializer(data.item, Item)
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        if (this.lines.length) {
            const line = this.lines[0]
            dict['item'] = line.item!.id ?? ''
            dict['total'] = line.total
            dict['quantity'] = line.quantity
        }
        dict['type'] = this.type
        return dict
    }

    static fk: FKType = {
        items: ['item'],
    }

    static dataType(): DataType {
        return 'adjustments'
    }

    get item(): Item | undefined {
        return this.getForeignRecord('item', Item)
    }
    set item(item: Item | undefined) {
        this.setForeignRecord('item', item)
    }
}
